import { Bot, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import '@/design-system/shell.css'

interface PublicViewToggleProps {
  currentView: 'human' | 'robot'
  humanUrl: string
  robotUrl: string
}

// Show the switcher near the top of the page and whenever the reader scrolls up;
// tuck it out of the way while they scroll down through content so it never sits
// on top of a heading or card.
const TOP_ZONE = 120 // px from the top where the toggle always stays visible
const SCROLL_DELTA = 6 // ignore sub-pixel/inertial jitter

export default function PublicViewToggle({
  currentView,
  humanUrl,
  robotUrl,
}: PublicViewToggleProps) {
  const isHumanView = currentView === 'human'
  const [hidden, setHidden] = useState(false)
  const lastY = useRef(0)

  useEffect(() => {
    lastY.current = window.scrollY

    // Cheap enough to run on every scroll event (two comparisons + a setState that
    // React bails out of when the value is unchanged) — no rAF throttle needed.
    const onScroll = () => {
      const y = window.scrollY
      const delta = y - lastY.current
      if (y < TOP_ZONE) {
        setHidden(false)
      } else if (Math.abs(delta) > SCROLL_DELTA) {
        setHidden(delta > 0) // hide while scrolling down, reveal while scrolling up
      }
      lastY.current = y
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    // Transform is set inline (not a Tailwind translate utility) so it reliably
    // slides the pill fully off the bottom edge; transition-transform animates it.
    <nav
      aria-label="Human and robot view switcher"
      style={{ transform: hidden ? 'translateY(150%)' : 'translateY(0)' }}
      className="ds-viewswitch"
    >
      <a
        href={humanUrl}
        aria-label="Human view"
        aria-current={isHumanView ? 'page' : undefined}
        className={`ds-viewswitch__side${isHumanView ? ' ds-viewswitch__side--on' : ''}`}
      >
        <User aria-hidden="true" />
        <span>Human</span>
      </a>
      <a
        href={robotUrl}
        aria-label="Robot view"
        aria-current={!isHumanView ? 'page' : undefined}
        className={`ds-viewswitch__side${!isHumanView ? ' ds-viewswitch__side--on' : ''}`}
      >
        <Bot aria-hidden="true" />
        <span>Robot</span>
      </a>
    </nav>
  )
}
